import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArmadaNav } from "@/components/ArmadaNav";
import { RankBadge, TierBadge, RoleBadge } from "@/components/Badges";
import GoldenPassBadge from "@/components/GoldenPassBadge";
import LineageTree from "@/components/LineageTree";
import B2BProvisioningPanel from "@/components/B2BProvisioningPanel";
import OIDCClientPanel from "@/components/OIDCClientPanel";
import { TESTID } from "@/lib/testIds";
import { Users, ShieldCheck, ShieldAlert, Activity, AlertTriangle, TrendingUp, Star, Sparkles, Anchor, Database, CheckCircle2, XCircle, Smartphone, ArrowRight, DollarSign, Coins, Crown } from "lucide-react";

const DASH_BG = "https://static.prod-images.emergentagent.com/jobs/106dfe78-39aa-4b25-90de-4fde5ec80481/images/8845c075997066c55713a94af0ab56ffd669bd37cdb34120c58099beaea6412c.png";

const HULLS = [
  { name: "Siren", color: "#F472B6" },
  { name: "SlayMarie", color: "#A78BFA" },
  { name: "Mercy", color: "#34D399" },
  { name: "Vanguard", color: "#60A5FA" },
  { name: "CyberOps", color: "#FBBF24" },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const isSovereign = user?.role === "sovereign";
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [airtable, setAirtable] = useState(null);
  const [revenue, setRevenue] = useState(null);

  useEffect(() => {
    if (!isSovereign) return;
    (async () => {
      try {
        const [s, e, a, r] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/login-events?limit=8"),
          api.get("/admin/airtable/status").catch(() => ({ data: null })),
          api.get("/admin/revenue/stats").catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        setRecentEvents(e.data);
        setAirtable(a.data);
        setRevenue(r.data);
      } catch {}
    })();
  }, [isSovereign]);

  // Role-routed homepages — Junior Admirals + Lineage Kin → vault; Guardians → bridge.
  if (user?.role === "junior_admiral" || user?.role === "lineage_kin") return <Navigate to="/junior-command" replace />;
  if (user?.role === "guardian") return <Navigate to="/parental-bridge" replace />;

  return (
    <div className="min-h-screen bg-armada-base relative">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img src={DASH_BG} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 dashboard-grid opacity-30" />
      </div>

      <div className="relative">
        <ArmadaNav />

        <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
          {/* Header */}
          <div className="mb-6 animate-fade-up">
            <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2">
              {isSovereign ? "GRAND SOVEREIGN DASHBOARD" : "COMMAND BRIEFING"}
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
              Welcome, <span className="gold-shimmer">{user?.full_name?.split(" ")[0] || "Commander"}</span>
            </h1>
            <div className="flex items-center gap-3 mt-3">
              <RoleBadge role={user?.role} />
              <RankBadge rank={user?.rank} size="lg" />
              <TierBadge tier={user?.revenue_tier} />
            </div>
          </div>

          {/* SECURITY / TOTP HERO CARD — always visible, prominent */}
          <GoldenPassBadge variant="card" />

          <Link
            to="/security"
            data-testid="dashboard-security-banner"
            className={`block mb-8 glass-strong rounded-xl p-6 animate-fade-up stagger-1 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D4AF37]/40 group ${
              user?.mfa_enabled
                ? "border-emerald-500/20"
                : "border-[#D4AF37]/30 shadow-[0_0_30px_rgba(212,175,55,0.15)]"
            }`}
          >
            <div className="flex items-center gap-5">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center shrink-0 ${
                user?.mfa_enabled
                  ? "bg-emerald-500/10 border border-emerald-500/40"
                  : "bg-[#D4AF37]/15 border border-[#D4AF37]/40 animate-pulse-gold"
              }`}>
                {user?.mfa_enabled ? (
                  <ShieldCheck className="w-8 h-8 text-emerald-400" strokeWidth={2} />
                ) : (
                  <ShieldAlert className="w-8 h-8 text-[#D4AF37]" strokeWidth={2} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-1">
                  PHASE 1 ARMOR HARDENING · TOTP
                </div>
                <div className="text-xl md:text-2xl font-black text-white tracking-tight">
                  {user?.mfa_enabled
                    ? "Two-Factor Armor: Active"
                    : "Two-Factor Armor: Not Yet Enabled"}
                </div>
                <p className="text-sm text-zinc-400 mt-1 font-light">
                  {user?.mfa_enabled
                    ? "Your hull is hardened with TOTP. Manage backup codes & device."
                    : "Secure your sovereign hull with Google Authenticator or Authy. One-tap setup."}
                </p>
              </div>
              <div className="hidden md:flex items-center gap-3 shrink-0">
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-bold tracking-wider text-sm transition-all ${
                  user?.mfa_enabled
                    ? "bg-transparent border border-emerald-500/40 text-emerald-300"
                    : "bg-[#D4AF37] text-black shadow-[0_0_20px_rgba(212,175,55,0.4)] group-hover:shadow-[0_0_30px_rgba(212,175,55,0.6)]"
                }`}>
                  <Smartphone className="w-4 h-4" />
                  {user?.mfa_enabled ? "Manage MFA" : "Enable Authenticator"}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </div>
            <div className="md:hidden mt-4 flex items-center justify-between text-sm font-bold text-[#D4AF37]">
              <span>{user?.mfa_enabled ? "Manage MFA" : "Enable Authenticator"}</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </Link>

          {isSovereign && !stats && (
            <div className="glass-strong rounded-xl p-6 mb-6 text-sm text-zinc-400 animate-fade-up">
              Loading fleet telemetry…
            </div>
          )}

          {isSovereign && <LineageTree />}

          {isSovereign && <B2BProvisioningPanel />}

          {isSovereign && <OIDCClientPanel />}

          {isSovereign && stats ? (
            <>
              {/* Stat bento */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard testId={TESTID.statTotal} label="Total Roster" value={stats.total_users} icon={Users} delay={1} />
                <StatCard testId={TESTID.statActive} label="Active" value={stats.active_users} icon={ShieldCheck} delay={2} />
                <StatCard testId={TESTID.statMfa} label="MFA Armored" value={stats.mfa_enabled} icon={Star} delay={3} />
                <StatCard testId={TESTID.statLogins24} label="Logins / 24h" value={stats.logins_24h} icon={TrendingUp} delay={4} />
              </div>

              {/* Bento grid: rank + tier + hulls + signals */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Rank distribution */}
                <div className="glass-strong rounded-xl p-6 animate-fade-up stagger-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] tracking-armada text-zinc-400 font-bold">RANK DISTRIBUTION</div>
                      <div className="text-lg font-bold text-white mt-1">Fleet Hierarchy</div>
                    </div>
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="space-y-3">
                    {Object.entries(stats.by_rank).map(([rank, count]) => {
                      const total = stats.total_users || 1;
                      const pct = Math.round((count / total) * 100);
                      return (
                        <div key={rank}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <RankBadge rank={rank} />
                            <span className="text-zinc-300 font-mono">{count}</span>
                          </div>
                          <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#A88729] to-[#D4AF37] transition-all duration-700"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Revenue split */}
                <div className="glass-strong rounded-xl p-6 animate-fade-up stagger-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] tracking-armada text-zinc-400 font-bold">60 / 30 / 10 SPLIT</div>
                      <div className="text-lg font-bold text-white mt-1">Revenue Tiers</div>
                    </div>
                    <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {["60", "30", "10"].map((tier) => (
                      <div key={tier} className="text-center p-3 rounded-md bg-black/40 border border-white/5">
                        <div className="text-3xl font-black gold-shimmer">{stats.by_tier[tier] || 0}</div>
                        <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">Tier {tier}%</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Roles */}
                <div className="glass-strong rounded-xl p-6 animate-fade-up stagger-7">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] tracking-armada text-zinc-400 font-bold">COMMAND STRUCTURE</div>
                      <div className="text-lg font-bold text-white mt-1">Roles</div>
                    </div>
                    <Anchor className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="space-y-3">
                    {Object.entries(stats.by_role).map(([role, count]) => (
                      <div key={role} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <RoleBadge role={role} />
                        <span className="text-xl font-bold text-white">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Signals (spans 2) */}
                <div className="glass-strong rounded-xl p-6 lg:col-span-2 animate-fade-up stagger-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] tracking-armada text-zinc-400 font-bold">RECENT SIGNALS</div>
                      <div className="text-lg font-bold text-white mt-1">Login Activity</div>
                    </div>
                    <Activity className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="divide-y divide-white/5">
                    {recentEvents.length === 0 && (
                      <div className="text-sm text-zinc-500 py-4">No recent activity.</div>
                    )}
                    {recentEvents.map((ev) => (
                      <div key={ev.id} className="flex items-center justify-between py-2.5 text-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${ev.success ? "bg-emerald-400" : "bg-red-400"}`} />
                          <span className="text-white truncate">{ev.email}</span>
                          <span className="text-[10px] tracking-wider uppercase text-zinc-500 font-semibold">{ev.event_type}</span>
                        </div>
                        <span className="text-[11px] text-zinc-500 font-mono shrink-0 ml-2">
                          {new Date(ev.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Threat alerts */}
                <div className="glass-strong rounded-xl p-6 animate-fade-up stagger-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] tracking-armada text-zinc-400 font-bold">THREAT MONITOR</div>
                      <div className="text-lg font-bold text-white mt-1">Failed (24h)</div>
                    </div>
                    <AlertTriangle className={`w-5 h-5 ${stats.failed_24h > 0 ? "text-amber-400" : "text-zinc-600"}`} />
                  </div>
                  <div className="text-5xl font-black text-white">{stats.failed_24h}</div>
                  <div className="text-[11px] tracking-wider uppercase text-zinc-500 font-semibold mt-2">
                    Failed Login Attempts
                  </div>
                </div>
              </div>

              {/* Subsidiary Hulls */}
              <div className="mt-8 glass-strong rounded-xl p-6 animate-fade-up">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] tracking-armada text-zinc-400 font-bold">SUBSIDIARY HULLS</div>
                    <div className="text-lg font-bold text-white mt-1">The Fleet</div>
                  </div>
                  <Anchor className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {HULLS.map((h) => (
                    <div key={h.name} className="p-4 rounded-lg bg-black/40 border border-white/5 hover:border-[#D4AF37]/30 transition-all">
                      <div className="w-2 h-2 rounded-full mb-2" style={{ background: h.color, boxShadow: `0 0 10px ${h.color}` }} />
                      <div className="text-sm font-bold text-white">{h.name}</div>
                      <div className="text-[10px] tracking-wider uppercase text-zinc-500 mt-1">SSO Linked</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Master Revenue Signal (Stripe → 60/30/10 or 75/15/10) */}
              {revenue && (
                <div className="mt-6 glass-strong rounded-xl p-6 animate-fade-up" data-testid="dashboard-revenue-widget">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] tracking-armada text-zinc-400 font-bold">MASTER REVENUE SIGNAL · STRIPE</div>
                      <div className="text-lg font-bold text-white mt-1">Multi-Mode Revenue Engine · 60/30/10 + 75/15/10</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {revenue.webhook_configured ? (
                        <span className="flex items-center gap-1.5 text-[10px] tracking-wider uppercase text-emerald-400 font-bold">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                          </span>
                          WEBHOOK ARMED
                        </span>
                      ) : (
                        <span className="text-[10px] tracking-wider uppercase text-zinc-500 font-bold">NOT CONFIGURED</span>
                      )}
                      <Coins className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="p-4 rounded-md bg-black/40 border border-white/5">
                      <div className="text-3xl font-black gold-shimmer">${revenue.amount_total.toFixed(2)}</div>
                      <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">Total Volume</div>
                    </div>
                    <div className="p-4 rounded-md bg-black/40 border border-white/5">
                      <div className="text-3xl font-black text-white">{revenue.total_events}</div>
                      <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">Transactions</div>
                    </div>
                    <div className="p-4 rounded-md bg-black/40 border border-white/5">
                      <div className="text-3xl font-black text-emerald-400">${revenue.amount_24h.toFixed(2)}</div>
                      <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">24H Volume</div>
                    </div>
                    <div className="p-4 rounded-md bg-black/40 border border-white/5">
                      <div className="text-3xl font-black text-white">{revenue.count_24h}</div>
                      <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">24H Count</div>
                    </div>
                  </div>
                  {/* 4-bucket split breakdown */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="p-4 rounded-md bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] tracking-wider text-[#D4AF37] uppercase font-bold">Sovereign · 60/75%</span>
                        <Star className="w-3 h-3 text-[#D4AF37]" />
                      </div>
                      <div className="text-2xl font-black gold-shimmer">${revenue.split_totals.sovereign.toFixed(2)}</div>
                    </div>
                    <div className="p-4 rounded-md bg-white/[0.03] border border-white/10">
                      <div className="text-[10px] tracking-wider text-white uppercase font-bold mb-1">Director · 28/13%</div>
                      <div className="text-2xl font-black text-white">${revenue.split_totals.director.toFixed(2)}</div>
                    </div>
                    <div className="p-4 rounded-md bg-white/[0.03] border border-white/10">
                      <div className="text-[10px] tracking-wider text-zinc-300 uppercase font-bold mb-1">Commander · 10%</div>
                      <div className="text-2xl font-black text-zinc-200">${revenue.split_totals.commander.toFixed(2)}</div>
                    </div>
                    <div className="p-4 rounded-md bg-gradient-to-br from-rose-500/15 via-fuchsia-500/10 to-[#D4AF37]/10 border border-rose-400/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] tracking-wider text-rose-200 uppercase font-bold">Heir Fund · 2%</span>
                        <Crown className="w-3 h-3 text-rose-300" />
                      </div>
                      <div className="text-2xl font-black text-rose-100">${revenue.split_totals.ancestral.toFixed(2)}</div>
                    </div>
                  </div>
                  {revenue.last_event && (
                    <div className="mt-4 pt-4 border-t border-white/5 text-[11px] text-zinc-500 font-mono flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-[#D4AF37] font-bold">Last signal:</span>
                      <span>{revenue.last_event.customer_email}</span>
                      <span>·</span>
                      <span>${(revenue.last_event.amount_total/100).toFixed(2)} {(revenue.last_event.currency||'').toUpperCase()}</span>
                      {revenue.last_event.product && <><span>·</span><span>{revenue.last_event.product}</span></>}
                      <span>·</span>
                      <span>{revenue.last_event.split_mode || '60/30/10'}</span>
                      <span>·</span>
                      <span>{new Date(revenue.last_event.processed_at).toLocaleTimeString()}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ── Sovereign Heir Fund ── Live operational fund (2% liquid, available now) */}
              {revenue?.heir_fund && (
                <div
                  data-testid="dashboard-heir-fund-widget"
                  className="mt-6 rounded-xl p-7 relative overflow-hidden animate-fade-up border border-rose-400/40 shadow-[0_0_50px_rgba(244,114,182,0.18)]"
                  style={{
                    background:
                      "radial-gradient(circle at 0% 0%, rgba(244,114,182,0.18) 0%, transparent 45%), radial-gradient(circle at 100% 100%, rgba(212,175,55,0.18) 0%, transparent 45%), rgba(10,10,10,0.75)",
                    backdropFilter: "blur(28px) saturate(180%)",
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] tracking-armada text-rose-200 font-bold mb-1">
                        <Crown className="w-3 h-3" />
                        DYNASTY · ANCESTRAL 2% PROTOCOL · LIVE FUND
                      </div>
                      <div className="text-2xl md:text-3xl font-black text-white tracking-tight">Sovereign Heir Fund</div>
                      <div className="text-xs text-rose-200/80 mt-1 font-light">
                        Live operational capital for Director K's children — automatic, liquid, ready to deploy.
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="flex items-center gap-1.5 text-[10px] tracking-wider uppercase font-bold text-emerald-300">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-300" />
                        </span>
                        LIQUID · AVAILABLE
                      </span>
                      <span className="text-[10px] tracking-wider uppercase font-bold text-zinc-400">
                        ROUTING: AUTOMATIC
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <div className="md:col-span-2 p-6 rounded-lg bg-black/50 border border-rose-400/20" data-testid="heir-fund-balance">
                      <div className="text-[10px] tracking-armada text-rose-200/80 font-bold mb-2">AVAILABLE BALANCE</div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl md:text-6xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#F9E79F] via-[#D4AF37] to-rose-300 tracking-tight">
                          ${revenue.heir_fund.available_balance.toFixed(2)}
                        </span>
                        <span className="text-sm text-zinc-400 font-mono">{revenue.heir_fund.currency}</span>
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-3 font-light">
                        Auto-routed at <span className="text-rose-200 font-bold">{revenue.heir_fund.tax_rate_pct}%</span> on every checkout · No vault, no lock — directly accessible.
                      </div>
                    </div>
                    <div className="grid grid-rows-2 gap-4">
                      <div className="p-4 rounded-lg bg-black/50 border border-white/10">
                        <div className="text-[10px] tracking-wider text-zinc-500 uppercase font-bold">Accumulated · 24H</div>
                        <div className="text-2xl font-black text-emerald-300 mt-1">${revenue.heir_fund.accumulated_24h.toFixed(2)}</div>
                      </div>
                      <div className="p-4 rounded-lg bg-black/50 border border-white/10">
                        <div className="text-[10px] tracking-wider text-zinc-500 uppercase font-bold">Status</div>
                        <div className="text-lg font-black text-emerald-300 mt-1 flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4" /> {revenue.heir_fund.status}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Airtable Master Ledger Telemetry */}
              {airtable && (
                <div className="mt-6 glass-strong rounded-xl p-6 animate-fade-up" data-testid="dashboard-airtable-widget">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] tracking-armada text-zinc-400 font-bold">MASTER LEDGER · AIRTABLE</div>
                      <div className="text-lg font-bold text-white mt-1">Fleet Telemetry Sync</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {airtable.configured && airtable.reachable ? (
                        <span className="flex items-center gap-1.5 text-[10px] tracking-wider uppercase text-emerald-400 font-bold">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                          </span>
                          LIVE
                        </span>
                      ) : airtable.configured ? (
                        <span className="text-[10px] tracking-wider uppercase text-red-400 font-bold flex items-center gap-1"><XCircle className="w-3 h-3" /> UNREACHABLE</span>
                      ) : (
                        <span className="text-[10px] tracking-wider uppercase text-zinc-500 font-bold">NOT CONFIGURED</span>
                      )}
                      <Database className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                  </div>
                  {airtable.configured && airtable.reachable && (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="p-3 rounded-md bg-black/40 border border-white/5">
                          <div className="text-3xl font-black gold-shimmer">{airtable.total_records}</div>
                          <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">Ledger Records</div>
                        </div>
                        <div className="p-3 rounded-md bg-black/40 border border-white/5">
                          <div className="text-3xl font-black text-emerald-400">{airtable.sync_log?.successes ?? 0}</div>
                          <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">Signals Pushed</div>
                        </div>
                        <div className="p-3 rounded-md bg-black/40 border border-white/5">
                          <div className={`text-3xl font-black ${airtable.sync_log?.failures > 0 ? "text-red-400" : "text-zinc-500"}`}>
                            {airtable.sync_log?.failures ?? 0}
                          </div>
                          <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">Failed Pushes</div>
                        </div>
                        <div className="p-3 rounded-md bg-black/40 border border-white/5">
                          <div className="text-sm font-bold text-white truncate">{airtable.sync_log?.last_sync?.event_type || "—"}</div>
                          <div className="text-[10px] tracking-wider text-zinc-500 uppercase mt-1">Last Signal</div>
                          {airtable.sync_log?.last_sync?.synced_at && (
                            <div className="text-[10px] text-zinc-400 font-mono mt-0.5">{new Date(airtable.sync_log.last_sync.synced_at).toLocaleTimeString()}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-2 font-mono">
                        <span className="text-[#D4AF37] font-bold">{airtable.table_name}</span>
                        <span>·</span>
                        <span>base {airtable.base_id?.slice(0, 8)}…</span>
                        <span>·</span>
                        <span>{airtable.field_names?.length} fields</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <CommanderDashboard user={user} />
          )}
        </main>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testId, delay = 1 }) {
  return (
    <div
      data-testid={testId}
      className={`glass-strong rounded-xl p-5 animate-fade-up stagger-${delay} hover:-translate-y-1 transition-transform duration-300`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] tracking-armada text-zinc-400 font-bold">{label}</div>
        <Icon className="w-4 h-4 text-[#D4AF37]" />
      </div>
      <div className="text-3xl md:text-4xl font-black text-white tabular-nums">{value}</div>
    </div>
  );
}

function CommanderDashboard({ user }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="glass-strong rounded-xl p-6 animate-fade-up stagger-1">
        <div className="text-[10px] tracking-armada text-zinc-400 font-bold mb-2">YOUR HULLS</div>
        <div className="text-lg font-bold text-white mb-4">Authorized Access</div>
        <div className="flex flex-wrap gap-2">
          {(user?.assigned_hulls || []).map((h) => (
            <span key={h} className="px-3 py-1.5 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-semibold">
              {h}
            </span>
          ))}
          {!user?.assigned_hulls?.length && (
            <span className="text-sm text-zinc-500">No hulls assigned. Contact Sovereign.</span>
          )}
        </div>
      </div>
      <div className="glass-strong rounded-xl p-6 animate-fade-up stagger-2">
        <div className="text-[10px] tracking-armada text-zinc-400 font-bold mb-2">YOUR DESIGNATION</div>
        <div className="text-lg font-bold text-white mb-4">Identity</div>
        <div className="space-y-2 text-sm">
          <Row k="Callsign" v={user?.callsign || "—"} />
          <Row k="Rank" v={<RankBadge rank={user?.rank} />} />
          <Row k="Revenue Tier" v={<TierBadge tier={user?.revenue_tier} />} />
          <Row k="Email" v={<span className="font-mono text-xs">{user?.email}</span>} />
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
      <span className="text-zinc-500 text-xs tracking-wider uppercase">{k}</span>
      <span className="text-white">{v}</span>
    </div>
  );
}
